import { MouseEvent, useEffect, useState } from 'react';
import { MfaType } from '@bumble/app/__generated__/data-contracts';
import { CardRows, btnSx } from '@bumble/app/components/CardRows';
import DialogConfirm from '@bumble/app/components/DialogConfirm';
import { Loading } from '@bumble/app/components/Loading';
import PageContent from '@bumble/app/components/PageContent';
import PageHeader from '@bumble/app/components/PageHeader';
import { useAccount } from '@bumble/app/hooks/use-account';
import { useApi } from '@bumble/app/hooks/use-api';
import { apiError } from '@bumble/app/utils/api-error';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import {
  Button,
  Card,
  CardContent,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

export const Account = () => {
  const api = useApi();
  const { account, isLoading, accountRefetch, session } = useAccount();
  const navigate = useNavigate();
  const [username, setUsername] = useState<string>(account?.username || '');
  const [password, setPassword] = useState<string>('');
  const [mfaEnabled, setMfaEnabled] = useState<boolean>(account?.mfa_enabled || false);
  const [metadata_url, setMetadataURL] = useState<string>(account?.username || '');
  const [showConfirmDelete, setShowConfirmDelete] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState(false);

  const {
    data: accountIDP,
    isLoading: isIDPLoading
  } = useQuery(
    ['accountIDP'],
    api.getAccountIdpApiV1GetAccountIdpGet.bind(null)
  );

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleMouseDownPassword = (event: MouseEvent<HTMLButtonElement>) =>
    void event.preventDefault();

  const updateAccount = useMutation(api.updateAccountApiV1UpdateAccountPost, {
    onSuccess() {
      toast.success('Account updated.');
      accountRefetch();
    },
    onError: apiError,
  });

  const updateAccountIdp = useMutation(api.updateAccountIdpApiV1UpdateAccountIdpPost, {
    onSuccess() {
      toast.success('Account IDP updated.');
    },
    onError: apiError,
  });

  // const updateIamAccount = useMutation(api.updateIamUserApiV1UpdateIamUserPost, {
  //   onSuccess() {
  //     toast.success('Account updated.');
  //     accountRefetch();
  //   },
  //   onError: apiError,
  // });

  const removeAccount = useMutation(api.removeAccountApiV1RemoveAccountPost, {
    onSuccess() {
      toast.success('Account removed.');
      navigate('/logout');
    },
    onError: apiError,
  });

  const enableEmailMfa = useMutation(api.accountEnableMfaApiV1AccountEnableMfaPost, {
    onSuccess() {
      toast.success('Multi-Factor Authentication enabled.');
    },
    onError: apiError,
  });

  const enableEmailMfaIam = useMutation(api.iamUserEnableMfaApiV1IamUserEnableMfaPost, {
    onSuccess() {
      toast.success('Multi-Factor Authentication enabled.');
    },
    onError: apiError,
  });

  const disableEmailMfa = useMutation(api.accountDisableMfaApiV1AccountDisableMfaPost, {
    onSuccess() {
      toast.success('Multi-Factor Authentication disabled.');
    },
    onError: apiError,
  });

  const disableEmailMfaIam = useMutation(api.iamUserDisableMfaApiV1IamUserDisableMfaPost, {
    onSuccess() {
      toast.success('Multi-Factor Authentication disabled.');
    },
    onError: apiError,
  });

  const handleUsernameSubmit = () => {
    if (account) {
      updateAccount.mutate({
        username,
      });
    }
  };

  const handlePasswordSubmit = () => {
    if (account) {
      updateAccount.mutate({
        username: account?.username,
        password,
      });
    }
  };

  const handleMfaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;
    if (!isChecked && session?.session_type === 'account') {
      disableEmailMfa.mutate({});
    } else {
      enableEmailMfa.mutate({ type: MfaType.Email });
    }
    if (!isChecked && session?.session_type === 'iam_user') {
      disableEmailMfaIam.mutate({});
    } else {
      enableEmailMfaIam.mutate({ type: MfaType.Email });
    }
    setMfaEnabled(isChecked);
  };

  const handleMetadataURLSubmit = () => {
    if (metadata_url) {
      updateAccountIdp.mutate({
        metalink: metadata_url,
      });
    }
  };

  const handleAccountDelete = () => {
    if (account) {
      removeAccount.mutate({
        username,
      });
    }
  };

  useEffect(() => {
    setUsername(account?.username || '');
    setMfaEnabled(account?.mfa_enabled || false);
  }, [account]);

  useEffect(() => {
    setMetadataURL(accountIDP?.data?.[0]?.metalink || '');
  }, [accountIDP]);

  if (isLoading || isIDPLoading) {
    return <Loading />;
  }
  return (
    <PageContent>
      <PageHeader pageName="Settings" />
      <Grid container spacing={5}>
        <Grid item xs={12} md={8}>
          <Card variant="outlined" elevation={0}>
            <CardRows
              label="Username"
              desc="The username cannot be updated at this time."
              value={
                <TextField
                  fullWidth
                  size="small"
                  id="username"
                  label="Username"
                  variant="outlined"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                />
              }
              action={
                <Button variant="contained" sx={btnSx} onClick={handleUsernameSubmit}>
                  Update
                </Button>
              }
            />
            <Divider />
            <CardRows
              label="Password"
              desc="Create a secure password to protect your account and ensure the confidentiality of your personal information."
              value={
                <TextField
                  fullWidth
                  size="small"
                  id="password"
                  label="Password"
                  variant="outlined"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  type={showPassword ? 'text' : 'password'}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowPassword}
                          onMouseDown={handleMouseDownPassword}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              }
              action={
                <Button variant="contained" sx={btnSx} onClick={handlePasswordSubmit}>
                  Update
                </Button>
              }
            />
            <Divider />
            <CardRows
              label="Multi-Factor Authentication"
              desc="Enable Multi-Factor Authentication to add an extra layer of security to your account. When enabled, you will be required to enter a verification code sent to your email address upon login."
              value={null}
              action={
                <FormControlLabel
                  value="top"
                  control={
                    <Switch color="primary" checked={mfaEnabled} onChange={handleMfaChange} />
                  }
                  label={mfaEnabled ? 'Enabled' : 'Disabled'}
                  labelPlacement="bottom"
                  sx={(theme) => ({ '&:hover': { color: theme.palette.primary.main } })}
                  componentsProps={{
                    typography: {
                      sx: (theme) => ({
                        fontSize: 12,
                        color: !mfaEnabled ? theme.palette.grey[600] : theme.palette.primary.main,
                      }),
                    },
                  }}
                />
              }
            />
            <Divider />
            <CardRows
              label="SAML integration"
              desc="Enable Single Sign on via SAML integration with your corporate IDP."
              value={
                <TextField
                  fullWidth
                  size="small"
                  id="metadata_url"
                  label="Bumblebee app metadata URL"
                  variant="outlined"
                  value={metadata_url}
                  onChange={(e) => setMetadataURL(e.target.value)}
                />
              }
              action={
                <Button variant="contained" sx={btnSx} onClick={handleMetadataURLSubmit}>
                  Update
                </Button>
              }
            />
            {session?.session_type === 'account' && (
              <>
                <Divider />
                <CardRows
                  color="error"
                  label="Delete Account"
                  desc="By clicking the 'Delete' button, your account will be permanently removed along with any associated connections. This action is irreversible and will result in the complete deletion of your account, including all data and connections. Please proceed with caution as this action cannot be undone."
                  value={null}
                  action={
                    <Button
                      variant="outlined"
                      color="error"
                      sx={btnSx}
                      onClick={() => setShowConfirmDelete(true)}
                    >
                      Delete
                    </Button>
                  }
                />
              </>
            )}
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card variant="outlined" elevation={0}>
            <CardContent>
              <Typography variant="subtitle1">What are Settings?</Typography>
              <Typography variant="body2">
                The Settings page empowers you to manage crucial aspects of your account.
                Here, you can update your username, create a strong password to enhance security,
                and even have the option to delete your account if needed.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      {showConfirmDelete && (
        <DialogConfirm
          open={showConfirmDelete}
          handleClose={() => setShowConfirmDelete(false)}
          handleConfirm={handleAccountDelete}
          title="Delete your account?"
          description="Are you certain you wish to proceed with resetting this endpoint? Please note that this action is irreversible and will result in the permanent reset of the endpoint. Proceeding with this action will reset all associated settings and configurations to their default state."
          confirmationPhrase="confirm"
        />
      )}
    </PageContent>
  );
};
